import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexCollectionModelOverview } from '@components/_rolex/CollectionModelOverview'
import { RolexCollectionPageContentBlock } from '@components/_rolex/CollectionPageContentBlock'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { MultipleItemResponse, SingleItemResponse, fetchContent } from '@lib/kontent'
import { fetchRolexV7Products } from '@lib/kontent/fetch-rolex-v7-products'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { RolexCollectionPage as RolexCollectionPageModel } from '@lib/kontent/models/RolexCollectionPage'
import { RolexCollectionPageContentBlock as RolexCollectionPageContentBlockModel } from '@lib/kontent/models/RolexCollectionPageContentBlock'
import { RolexImage as RolexImageModel } from '@lib/kontent/models/RolexImage'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { RolexProductPageV72024 } from '@lib/kontent/models/RolexProductPageV72024'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

const translateMap = {
  '1908': 'family_1908',
}

type RolexCollectionPageProps = SingleItemResponse<RolexCollectionPageModel> & {
  products: MultipleItemResponse<RolexProductPageV72024>
  fid: string
  kontentFid: string
}

const RolexCollectionPage: NextPage<RolexCollectionPageProps> = ({
  item,
  fid,
  kontentFid,
  products,
  modular_content,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['rolex', 'navigation'])
  const { locale } = useRouter()
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  )
  const heroBanner = modular_content[item.elements.hero_image.modular_content[0]]
    .elements as RolexImageModel
  const titlePlusDescription = modular_content[item.elements.title___description.modular_content[0]]
    .elements as RolexPageIntroModel
  const firstContentBlocks = item.elements.content_block_items.modular_content.map(
    (key) => modular_content[key].elements,
  ) as RolexCollectionPageContentBlockModel[]

  return (
    <Page
      {...pickSeoProps(item.elements)}
      slug={`rolex/${fid}`}
      languageAlternates={[
        { hrefLang: 'nl', href: `/rolex/${fid}` },
        { hrefLang: 'en', href: `/en/rolex/${fid}` },
        { hrefLang: 'de', href: `/de/rolex/${fid}` },
      ]}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="family page" productFamily={fid}>
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.watches', { ns: 'rolex' }),
              href: `/rolex${t('rolex.watches', { ns: 'navigation' })}`,
            },
          ]}
          currentPageTitle={item.elements.collection_name.value}
        />
        <RolexImage
          size="xl"
          mobileImage={heroBanner.mobile_image}
          desktopImage={heroBanner.desktop_image}
          className="!mb-0"
        />
        <RolexPageIntro
          className="bg-rolexLightBeige"
          title={titlePlusDescription.title.value}
          description={titlePlusDescription.description}
          titleTag={titlePlusDescription.title_tag.value[0].codename}
        />
        {firstContentBlocks.map((block, key) => (
          <RolexCollectionPageContentBlock key={key} {...block} modularContent={modular_content} />
        ))}
        <RolexCollectionModelOverview
          initialData={products}
          language={language}
          fid={fid}
          kontentFid={kontentFid}
          title={item.elements.collection_name.value}
        />
        <KeepExploring className="py-[30px] md:py-[60px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export default RolexCollectionPage

export const getStaticProps: GetStaticProps = async ({ locale, params, preview }) => {
  let fid = params.fid as string
  const language = ['sg', 'de'].includes(locale) ? 'en' : locale
  const translatedFid = translateMap[fid] || fid
  const underscoreFid = translatedFid.replaceAll('-', '_')
  try {
    const content: SingleItemResponse<RolexCollectionPageModel> = await getPageByTypeAndUrlSlug({
      type: 'rolex_collection_page',
      urlSlug: fid,
      language: language,
      preview,
    })

    const products = await fetchRolexV7Products({
      language,
      fid: underscoreFid,
    })

    return {
      props: {
        ...content,
        products,
        fid,
        kontentFid: underscoreFid,
        ...(await getServerSideTranslations(locale)),
      },
    }
  } catch (error) {
    return {
      notFound: true,
    }
  }
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (process.env.VERCEL_ENV !== 'production') {
    return { paths: [], fallback: 'blocking' }
  }

  let paths = []
  for (const locale of locales) {
    const collectionPages: MultipleItemResponse<RolexCollectionPageModel> = await fetchContent({
      path: '',
      params: {
        elements: ['url_slug'],
        'system.type[in]': ['rolex_collection_page'],
        language: locale,
      },
    })

    collectionPages.items.forEach((item) => {
      if (item.system.language === locale) {
        paths.push({
          params: {
            fid: item.elements.url_slug.value,
          },
          locale,
        })
      }
    })
  }

  return { paths, fallback: 'blocking' }
}
